/**
 * Film Issue Codes Configuration
 * 
 * Struktur:
 * - code: Eindeutiger Code-Name
 * - category: 'EMPFANG' | 'LABOR'
 * - severity: '🔴' (Kritisch) | '🟠' (Mittel) | '🟡' (Info) | '🔵' (Hinweis)
 * - title: Kurzer Titel
 * - description: Was ist passiert?
 * - causes: Mögliche Ursachen (Array)
 * - prevention: So vermeidest du das beim nächsten Mal
 * - helpUrl: Link zu Hilfe & Support
 */

const FILM_ISSUE_CODES = {
    // ========== EMPFANG ==========
    FILM_NOT_PROPERLY_LOADED: {
        category: 'EMPFANG',
        severity: '🔴',
        title: 'Film nicht richtig eingelegt',
        description: 'Der Film wurde in der Kamera nicht korrekt transportiert.',
        causes: [
            'Film nicht richtig eingehängt',
            'Film hat sich beim Spannen nicht mitgedreht',
            'Rückspulknopf versehentlich gedrückt'
        ],
        prevention: [
            'Beim Einlegen prüfen, ob sich der Film beim Weiterdrehen mitbewegt',
            'Nach 1–2 Fotos kurz kontrollieren'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    NON_C41_FILM: {
        category: 'EMPFANG',
        severity: '🟡',
        title: 'Kein C41-Film',
        description: 'Dieser Film kann nicht im C-41 Standardprozess entwickelt werden.',
        causes: [
            'Schwarzweiss- oder E6 verwendet',
            'Filmtyp verwechselt'
        ],
        prevention: [
            'Das Wiederfaenger Lab entwickelt nur Film im C41 Prozess',
            'Andere Entwicklungsprozesse (z.B. E6 oder Schwarz-Weiss) dauern länger, da sie extern gemacht werden'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    WRONG_FORMAT: {
        category: 'EMPFANG',
        severity: '🟡',
        title: 'Falsches Filmformat',
        description: 'Das Filmformat passt nicht zu unserem Entwicklungsprozess.',
        causes: [
            'Wir entwickeln nur 35mm C-41'
        ],
        prevention: [
            'Mit der FAENGER immer 35mm-Film verwenden'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    PACKAGING_DAMAGED: {
        category: 'EMPFANG',
        severity: '🟡',
        title: 'Verpackung beschädigt',
        description: 'Die Filmpatrone oder Verpackung war bei Ankunft beschädigt.',
        causes: [
            'Transportschaden',
            'Film ungeschützt verschickt'
        ],
        prevention: [
            'Film gut geschützt im Rücksende-Couvert versenden',
            'Verwende unsere gepolsterten Rücksende-Couverts'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    // ========== LABOR ==========
    FILM_FULL_OVEREXPOSED: {
        category: 'LABOR',
        severity: '🔴',
        title: 'Film komplett überbelichtet',
        description: 'Der gesamte Film ist sehr hell geworden.',
        causes: [
            'Falscher Film für die Kamera',
            'Sehr starkes Licht'
        ],
        prevention: [
            'Empfohlenen Film für die FAENGER verwenden (ISO 400 oder 800)',
            'Nicht direkt in grelles Licht fotografieren'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_FULL_UNDEREXPOSED: {
        category: 'LABOR',
        severity: '🔴',
        title: 'Film komplett unterbelichtet',
        description: 'Der gesamte Film ist sehr dunkel / schwarz geworden.',
        causes: [
            'Blitz nicht eingeschaltet',
            'Batterie leer',
            'Zu wenig Licht',
            'Zu tiefer ISO verwendet'
        ],
        prevention: [
            'Bei Innenräumen immer Blitz einschalten',
            'Subjekt mit Blitz innerhalb von 1-3 Meter fotografieren',
            'Batterie regelmässig wechseln'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_PART_OVEREXPOSED: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Film teilweise überbelichtet',
        description: 'Einige Bilder sind zu hell geworden.',
        causes: [
            'Wechselnde Lichtverhältnisse',
            'Sehr starkes Sonnenlicht',
            'Zu hoher ISO bei genügend (Sonnen)licht'
        ],
        prevention: [
            'Motiv nicht direkt gegen die Sonne fotografieren',
            'Tieferer ISO bei sehr hellen/sonnigen Lichtverhältnissen verwenden',
            'Kamera erst öffnen, wenn der Film komplett zurückgespult ist'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_PART_UNDEREXPOSED: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Film teilweise unterbelichtet',
        description: 'Einige Bilder sind sehr dunkel geworden.',
        causes: [
            'Blitz vergessen',
            'Zu grosse Distanz',
            'Batterie schwach'
        ],
        prevention: [
            'Blitz immer einschalten',
            'Abstand zum Subjekt ca. 1–2 Meter, maximal 3 Meter',
            'Batterie prüfen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_OPENED_TOO_EARLY: {
        category: 'LABOR',
        severity: '🔴',
        title: 'Kamera zu früh geöffnet',
        description: 'Die Kamera wurde geöffnet, bevor der Film vollständig zurückgespult war.',
        causes: [
            'Film war noch nicht komplett zurückgespult',
            'Unsicherheit, ob der Film fertig ist'
        ],
        prevention: [
            'Mit 20–30 Kurbelumdrehungen bist du auf der sicheren Seite',
            'Rückspulen, bis kein Widerstand mehr spürbar ist',
            'Typisches Rückspulgeräusch muss verstummen',
            'Kamera erst danach öffnen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    LIGHT_LEAKS: {
        category: 'LABOR',
        severity: '🔵',
        title: 'Lichteinfall (Light Leaks)',
        description: 'Licht-Artefakte sind auf den Film gelangt.',
        causes: [
            'Kamera vor dem Rückspulen geöffnet',
            'Film beschädigt',
            'Kamera ist undicht'
        ],
        prevention: [
            'Kamera erst öffnen, wenn der Film komplett zurückgespult ist',
            'Light Leaks können auch ein gewünschter Effekt sein'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_NOT_TRANSPORTED_CORRECTLY: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Filmtransport fehlerhaft',
        description: 'Die Bilder liegen unregelmässig oder überlappen sich.',
        causes: [
            'Film nicht sauber gespannt',
            'Transport blockiert'
        ],
        prevention: [
            'Film korrekt einlegen',
            'Beim Weiterdrehen keinen Widerstand erzwingen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FILM_NOT_REWOUND_PROPERLY: {
        category: 'LABOR',
        severity: '🔴',
        title: 'Film nicht richtig zurückgespult',
        description: 'Der Film wurde beim Rückspulen beschädigt.',
        causes: [
            'Film nicht vollständig eingezogen',
            'Kamera zu früh geöffnet'
        ],
        prevention: [
            'Rückspulen bis kein Widerstand mehr spürbar ist'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    PARTIALLY_NOT_EXPOSED: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Teilweise nicht belichtet',
        description: 'Teile des Films sind komplett leer geblieben.',
        causes: [
            'Film falsch eingelegt',
            'Film zu früh zurückgespult',
            'Objektivdeckel vergessen wegzunehmen'
        ],
        prevention: [
            'Vor dem Fotografieren prüfen, ob das Objektiv frei ist',
            'Filmrolle komplett durchschiessen (Bildzähler beachten)'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    CAMERA_DEFECT: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Kamera Defekt vermutet',
        description: 'Das Fehlerbild deutet auf einen Kameradefekt hin.',
        causes: [
            'Mechanisches Problem',
            'Elektronik reagiert nicht'
        ],
        prevention: [
            'Kamera nicht weiterverwenden',
            'Bei uns melden'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    BLANK: {
        category: 'LABOR',
        severity: '🔴',
        title: 'Film unbelichtet',
        description: 'Der Film ist komplett leer.',
        causes: [
            'Film wurde nicht transportiert',
            'Kamera hat nicht ausgelöst'
        ],
        prevention: [
            'Nach den ersten Fotos prüfen, ob der Film dreht',
            'Der Rückspulhebel (bei der FAENGER oben links) muss beim Vorspulen mitdrehen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    SCRATCHES: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Kratzer',
        description: 'Feine Linien sind auf den Bildern sichtbar.',
        causes: [
            'Schmutz in der Kamera',
            'Staub beim Einlegen'
        ],
        prevention: [
            'Kamera regelmässig mit Blasebalg reinigen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    FOGGED: {
        category: 'LABOR',
        severity: '🟠',
        title: 'Schleier / Fogging',
        description: 'Der Film zeigt einen grauen Schleier (XRAY oder Abgelaufener Film).',
        causes: [
            'Abgelaufener Film',
            'Hitze oder Röntgenstrahlung'
        ],
        prevention: [
            'Film kühl lagern',
            'Nicht ins Aufgabegepäck legen'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    DEVELOPMENT_ISSUE: {
        category: 'LABOR',
        severity: '🟡',
        title: 'Entwicklungsfehler',
        description: 'Während der Entwicklung gab es ein technisches Problem.',
        causes: [
            'Prozessabweichung im Labor'
        ],
        prevention: [
            'Hier ist nichts zu tun, das liegt bei uns'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    },

    OTHER: {
        category: 'EMPFANG',
        severity: '🟡',
        title: 'Sonstiges',
        description: 'Ein Problem ist aufgetreten, das keiner Standard-Kategorie zugeordnet werden kann.',
        causes: [
            'Individueller Fall'
        ],
        prevention: [
            'Siehe Details im Bericht'
        ],
        helpUrl: 'https://wiederfaenger.ch/blogs/filmdoktor'
    }
};

// Export to window for use in film-gallery-v3.js
window.FILM_ISSUE_CODES = FILM_ISSUE_CODES;

// Also support CommonJS if needed
if (typeof module !== 'undefined' && module.exports) {
    module.exports = FILM_ISSUE_CODES;
}
